import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Search, Filter, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CaseLibrary() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [requestText, setRequestText] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [filters, setFilters] = useState({
    domain: '',
    sector: '',
    geography: '',
    year: '',
    length_pages: '',
    product_type: '',
    keywords: ''
  });

  const hasActiveQuery = Boolean(
    search.trim() || Object.values(filters).some(v => v && String(v).trim())
  );

  useEffect(() => {
    if (!hasActiveQuery) {
      setCases([]);
      setLoading(false);
      return;
    }
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filters]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (filters.domain) params.append('domain', filters.domain);
      if (filters.sector) params.append('sector', filters.sector);
      if (filters.geography) params.append('geography', filters.geography);
      if (filters.year) params.append('year', filters.year);
      if (filters.length_pages) params.append('length_pages', filters.length_pages);
      if (filters.product_type) params.append('product_type', filters.product_type);
      if (filters.keywords) params.append('search', filters.keywords); // Keywords use search

      const response = await axios.get(`${API}/cases?${params}`);
      setCases(response.data);
    } catch (error) {
      toast.error('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const handleCaseRequest = async () => {
    if (!requestText.trim()) return;
    setSubmittingRequest(true);
    try {
      await axios.post(`${API}/cases/request`, { message: requestText.trim() });
      toast.success('Your request has been submitted!');
      setRequestText('');
      setRequestSubmitted(true);
      setTimeout(() => setRequestSubmitted(false), 5000);
    } catch {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmittingRequest(false);
    }
  };


  const clearFilters = () => {
    setFilters({
      domain: '',
      sector: '',
      geography: '',
      year: '',
      length_pages: '',
      product_type: '',
      keywords: ''
    });
    setSearch('');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="fixed top-0 w-full z-50 glassmorphism">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="text-2xl font-serif font-bold tracking-tight text-slate-900" data-testid="logo-link">
              The Case Verse
            </Link>
            <div className="flex items-center gap-6">
              <Link to="/dashboard" className="text-slate-700 hover:text-slate-900" data-testid="nav-dashboard-link">Dashboard</Link>
              <Link to="/subscription" className="text-slate-700 hover:text-slate-900" data-testid="nav-subscription-link">Subscription</Link>
              {user?.role === 'admin' && <Link to="/admin" className="text-slate-700 hover:text-slate-900" data-testid="nav-admin-link">Admin</Link>}
              {['instructor', 'institution_admin', 'admin'].includes(user?.role) && (
                <>
                  <Link to="/book-training" className="text-slate-700 hover:text-slate-900" data-testid="nav-book-training-link">Book Training</Link>
                  <Link to="/request-case" className="text-slate-700 hover:text-slate-900" data-testid="nav-request-case-link">Request Case</Link>
                </>
              )}
              <button onClick={logout} className="px-4 py-2 bg-slate-900 text-white rounded hover:bg-slate-800" data-testid="logout-button">Logout</button>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-serif font-bold tracking-tight text-slate-900 mb-4" data-testid="page-title">Case Library</h1>
            {user?.is_trial && user?.role !== 'admin' && (
              (() => {
                const trialEnd = user.trial_end ? new Date(user.trial_end) : null;
                const isExpired = trialEnd && new Date() > trialEnd;
                
                return isExpired ? (
                  <div className="bg-red-50 border border-red-200 rounded p-4 mb-6" data-testid="trial-expired-notice">
                    <p className="text-sm text-red-900">
                      Your 7-day free trial has expired. <Link to="/subscription" className="underline font-medium">Subscribe now</Link> to continue accessing case studies.
                    </p>
                  </div>
                ) : (
                  <div className="bg-orange-50 border border-orange-200 rounded p-4 mb-6" data-testid="trial-notice">
                    <p className="text-sm text-orange-900">
                      You are on a free trial with access to sample cases. <Link to="/subscription" className="underline font-medium">Subscribe</Link> for full access.
                    </p>
                  </div>
                );
              })()
            )}
          </div>

          <div className="mb-8 space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search cases..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                data-testid="search-input"
              />
            </div>

            <div className="bg-white p-6 rounded border border-slate-200" data-testid="filters-panel">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <Filter className="w-5 h-5" />
                  Filter Cases
                </h3>
                <button onClick={clearFilters} className="text-sm text-orange-600 hover:text-orange-700" data-testid="clear-filters-button">
                  Clear all
                </button>
              </div>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Domain</label>
                  <select
                    value={filters.domain}
                    onChange={(e) => setFilters({ ...filters, domain: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="domain-filter"
                  >
                    <option value="">All Domains</option>
                    <option value="Business Communication and Soft Skills">Business Communication and Soft Skills</option>
                    <option value="Economics">Economics</option>
                    <option value="Entrepreneurship">Entrepreneurship</option>
                    <option value="Ethics- Sustainability- Governance- Climate">Ethics- Sustainability- Governance- Climate</option>
                    <option value="Family Business">Family Business</option>
                    <option value="Finance">Finance</option>
                    <option value="Human Resources Management">Human Resources Management</option>
                    <option value="Marketing">Marketing</option>
                    <option value="Operations and Logistics">Operations and Logistics</option>
                    <option value="Strategy">Strategy</option>
                    <option value="Technology">Technology</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Geography</label>
                  <select
                    value={filters.geography}
                    onChange={(e) => setFilters({ ...filters, geography: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="geography-filter"
                  >
                    <option value="">All Geographies</option>
                    <option value="India">India</option>
                    <option value="China">China</option>
                    <option value="US">United States</option>
                    <option value="Europe">Europe</option>
                    <option value="Asia">Asia</option>
                    <option value="Africa">Africa</option>
                    <option value="Latin America">Latin America</option>
                    <option value="Middle East">Middle East</option>
                    <option value="Global">Global</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Sector</label>
                  <select
                    value={filters.sector}
                    onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="sector-filter"
                  >
                    <option value="">All Sectors</option>
                    <option value="Advertising, Research and Consulting">Advertising, Research and Consulting</option>
                    <option value="Auto">Auto</option>
                    <option value="Aviation">Aviation</option>
                    <option value="Banking and Finance">Banking and Finance</option>
                    <option value="Healthcare and Pharma">Healthcare and Pharma</option>
                    <option value="Hospitality and Tourism">Hospitality and Tourism</option>
                    <option value="Media and Entertainment">Media and Entertainment</option>
                    <option value="Operations and Logistics">Operations and Logistics</option>
                    <option value="Primary and Core">Primary and Core</option>
                    <option value="Realty and Infra">Realty and Infra</option>
                    <option value="Retail">Retail</option>
                    <option value="StartUps">StartUps</option>
                    <option value="Technology">Technology</option>
                    <option value="Telecom">Telecom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Product Type</label>
                  <select
                    value={filters.product_type}
                    onChange={(e) => setFilters({ ...filters, product_type: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="product-type-filter"
                  >
                    <option value="">All Types</option>
                    <option value="Case Study">Case Study</option>
                    <option value="Simulation">Simulation</option>
                    <option value="Games">Games</option>
                    <option value="Role Plays">Role Plays</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Year</label>
                  <select
                    value={filters.year}
                    onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="year-filter"
                  >
                    <option value="">All Years</option>
                    {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Length (Pages)</label>
                  <select
                    value={filters.length_pages}
                    onChange={(e) => setFilters({ ...filters, length_pages: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="length-filter"
                  >
                    <option value="">All Lengths</option>
                    <option value="< 2">&lt; 2 pages</option>
                    <option value="2-5">2-5 pages</option>
                    <option value="6-10">6-10 pages</option>
                    <option value="11-15">11-15 pages</option>
                    <option value="> 15">&gt; 15 pages</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Key Words</label>
                  <input
                    type="text"
                    value={filters.keywords}
                    onChange={(e) => setFilters({ ...filters, keywords: e.target.value })}
                    placeholder="Search by keywords..."
                    className="w-full px-4 py-2 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-900"
                    data-testid="keywords-filter"
                  />
                </div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
            </div>
          ) : !hasActiveQuery ? (
            <div className="text-center py-16 bg-white border border-dashed border-slate-300 rounded-xl" data-testid="empty-search-prompt">
              <Search className="w-10 h-10 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-700 font-medium">Use the search bar or filters above to find cases.</p>
              <p className="text-slate-500 text-sm mt-2">Try searching by keyword, domain, or geography.</p>
            </div>
          ) : cases.length === 0 ? (
            <div className="text-center py-12" data-testid="no-cases-message">
              <p className="text-slate-600">No cases found. Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-6" data-testid="cases-grid">
              {cases.map((caseItem) => (
                <div
                  key={caseItem.id}
                  className="bg-white p-6 rounded border border-slate-200 case-card cursor-pointer"
                  onClick={() => navigate(`/cases/${caseItem.id}`)}
                  data-testid={`case-card-${caseItem.id}`}
                >
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 text-xs font-mono rounded">
                      {Array.isArray(caseItem.domain) ? caseItem.domain.join(', ') : caseItem.domain}
                    </span>
                    {caseItem.is_sample && (
                      <span className="ml-2 inline-block px-3 py-1 bg-orange-100 text-orange-700 text-xs font-mono rounded">
                        SAMPLE
                      </span>
                    )}
                  </div>
                  <h3 className="text-xl font-serif font-semibold mb-2">{caseItem.title}</h3>
                  <p className="text-slate-600 text-sm mb-4 line-clamp-2">{caseItem.description}</p>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                    <span>{caseItem.geography}</span>
                    <span>•</span>
                    <span>{caseItem.year}</span>
                    <span>•</span>
                    <span>{Array.isArray(caseItem.level) ? caseItem.level.join(', ') : caseItem.level}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Case Request Box */}
        {user && (
          <div className="mt-10 bg-white rounded-xl border border-slate-200 p-6" data-testid="case-request-section">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Can't find what you're looking for?</h3>
            <p className="text-sm text-slate-600 mb-4">Describe what you need and our team will look into it.</p>
            <div className="flex gap-3">
              <textarea
                value={requestText}
                onChange={(e) => setRequestText(e.target.value)}
                placeholder="Describe the case study or content you're looking for..."
                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none text-sm"
                rows={3}
                data-testid="case-request-input"
              />
              <div className="flex flex-col justify-end">
                <button
                  onClick={handleCaseRequest}
                  disabled={submittingRequest || !requestText.trim()}
                  className="px-6 py-3 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  data-testid="case-request-submit"
                >
                  {submittingRequest ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </div>
            {requestSubmitted && (
              <p className="mt-3 text-sm text-green-600 font-medium" data-testid="case-request-success">
                Your request has been submitted successfully. Our team will look into it.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}